/* ============================================================
   component-product-reviews.js
   Section avis produit — bouton "Donner mon avis" délègue au SDK natif Judge.me
   (Theme App Extension activé via app embed + Review Widget block).

   Iter 2026-05-08 final — refacto suite à diagnostic API publique :
     - Le fallback EDJ HTML form a été retiré (~600 lignes) : Judge.me drop
       silencieusement les soumissions API publiques anonymes (HTTP 201
       sans persistance côté queue).
     - Le bouton "Donner mon avis" porte la classe `.jdgm-write-rev-link` +
       `data-product-id` — l'event delegation du SDK Judge.me s'en charge
       automatiquement quand l'app embed est actif et le widget rendu.
     - On garde ici uniquement : init filtres pills + load more + diagnostic
       léger pour avertir si SDK absent (preview HTTP local).
   ============================================================ */

(function () {
  'use strict';

  /* ── Détecte si Judge.me a complètement hydraté son SDK ──
     L'app embed Judge.me (settings_data.json blocks judgeme_core) charge
     window.jdgm + le module event delegation. Le widget invisible
     `.jdgm-rev-widg` (rendu par sections/product-reviews.liquid) active
     le scan des `.jdgm-write-rev-link` dans le DOM.

     Cette fonction sert au diagnostic uniquement (console.warn si absent).
     Le SDK gère le clic — on n'intercepte plus rien. */
  function hasJudgemeSdk() {
    if (!window.jdgm) return false;
    if (typeof window.jdgm.openWriteReviewForm === 'function') return true;
    if (window.jdgm.JdgmModule) return true;
    return !!document.querySelector('.jdgm-write-rev-form, .jdgm-rev-widg .jdgm-rev-widg-h');
  }

  /* ── Ouvre le modal Judge.me — robuste API v2 ET v3 ──
     Diagnostic 2026-05-08 : le SDK Judge.me v3 a renommé openWriteReviewForm
     en openForm. De plus, le SDK ne bind pas toujours nos boutons EDJ via
     event delegation (selon hydration timing).
     Stratégie résiliente :
       1. Si jdgm.openForm (v3) → appel direct
       2. Si jdgm.openWriteReviewForm (v2) → appel direct
       3. Sinon : .click() sur le bouton natif du widget block officiel
          (.jdgm-review-widget [class*="jm-button--primary"] OU
           .jdgm--done-setup-widget [class*="jm-button"]).
          C'est le déclencheur 100% fiable car attaché au widget hydraté.
       4. Fallback ultime : warn console + lien vers /products/{handle} */
  function openJudgemeWriteReview(productId, productHandle) {
    try {
      if (window.jdgm && typeof window.jdgm.openForm === 'function') {
        window.jdgm.openForm(productId);
        return true;
      }
      if (window.jdgm && typeof window.jdgm.openWriteReviewForm === 'function') {
        window.jdgm.openWriteReviewForm(productId);
        return true;
      }
    } catch (e) { /* fall through */ }

    /* Fallback robuste — clic sur le bouton natif du widget officiel */
    var nativeBtn = document.querySelector(
      '.jdgm-review-widget .jm-button--primary, ' +
      '.jdgm--done-setup-widget .jm-button--primary, ' +
      '.jdgm-review-widget [class*="jm-button"][class*="--primary"], ' +
      '.jdgm-rev-widg .jdgm-write-rev-link'
    );
    if (nativeBtn && typeof nativeBtn.click === 'function') {
      nativeBtn.click();
      return true;
    }

    /* eslint-disable-next-line no-console */
    console.warn('[EDJ] Impossible d\'ouvrir le modal Judge.me. Vérifications : ' +
      '(1) app embed Judge.me actif sur le thème, ' +
      '(2) Review Widget block ajouté à la section main-product, ' +
      '(3) page consultée via storefront natif (pas preview HTTP cross-origin).');
    return false;
  }

  function initSection(section) {
    if (!section || section.dataset.productReviewsInit === '1') return;
    section.dataset.productReviewsInit = '1';

    /* ── Bouton "Donner mon avis" — délégation robuste vers SDK Judge.me ──
       On capture le clic sur nos boutons [data-edj-write-review] (qui portent
       aussi la classe .jdgm-write-rev-link pour event delegation) et on
       appelle openJudgemeWriteReview qui essaye plusieurs APIs. */
    var writeBtns = section.querySelectorAll('[data-edj-write-review]');
    writeBtns.forEach(function (btn) {
      btn.addEventListener('click', function (ev) {
        var pid = btn.getAttribute('data-product-id') || '';
        var handle = section.getAttribute('data-product-handle') || '';
        /* On ne preventDefault QUE si on a réussi à ouvrir un modal.
           Sinon on laisse passer le clic native (au cas où Judge.me aurait
           posé son propre handler en event delegation). */
        var opened = openJudgemeWriteReview(pid, handle);
        if (opened) ev.preventDefault();
      });
    });

    /* ── Filter pills (5★, 4★, photos, vérifiés…) ── */
    var pills = section.querySelectorAll('.product-reviews__pill');
    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        pills.forEach(function (p) {
          p.classList.remove('is-active');
          p.setAttribute('aria-selected', 'false');
        });
        pill.classList.add('is-active');
        pill.setAttribute('aria-selected', 'true');
      });
    });

    /* ── Load more ── */
    var loadBtn = section.querySelector('[data-load-more]');
    if (loadBtn) {
      loadBtn.addEventListener('click', function () {
        var grid = section.querySelector('[data-reviews-grid]');
        if (!grid) return;
        var hidden = grid.querySelectorAll('.product-reviews__card.is-hidden');
        hidden.forEach(function (card) {
          card.classList.remove('is-hidden');
        });
        loadBtn.style.display = 'none';
      });
    }
  }

  function initAll() {
    document.querySelectorAll('.product-reviews').forEach(initSection);

    /* ── Diagnostic léger (console only, no UI) ──
       2.5s après load, si SDK Judge.me toujours pas hydraté ET au moins
       un bouton .jdgm-write-rev-link est présent dans le DOM, on log un
       avertissement pour que les devs sachent que le clic ne marchera pas
       (cas preview HTTP local, ou app embed désactivé en prod). */
    setTimeout(function () {
      if (document.querySelector('.jdgm-write-rev-link') && !hasJudgemeSdk()) {
        // eslint-disable-next-line no-console
        console.warn(
          '[EDJ] Judge.me SDK non détecté — le bouton "Donner mon avis" ne fonctionnera pas.\n' +
          'Vérifications : (1) app embed Judge.me activé sur le thème actif, ' +
          '(2) Review Widget block ajouté à la section main-product, ' +
          '(3) page consultée via le storefront natif (pas un preview HTTP cross-origin).'
        );
      }
    }, 2500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('.product-reviews');
    if (section) initSection(section);
  });
})();
